/**
 * sonicfiAdopt -- types and pure view helpers for the SonicFi/CyberTan RSW adopt preview (OP#1838).
 *
 * WHY (owner decision 2026-10-13, OP#1820): the SonicFi RSW226G/RSW210G have never had a config of
 * record. They boot their factory uCentral document every time, so hand VLAN edits (VLAN 1000 at
 * Jedi Home, OP#1801) vanish at the next reboot, and a first controller push would replace the
 * whole switch config blind. The preview shows, READ-ONLY, the config of record Meridian would
 * build from what the switch runs, and two diffs:
 *   - running vs candidate: must be EMPTY (the first push changes no forwarding);
 *   - boot vs candidate:    what becomes durable (the VLANs a reboot would drop today).
 * Nothing in this flow pushes. Backend: GET/POST /api/v2/devices/{serial}/switch/adopt-preview
 * (services/sonicfi_adopt + services/sonicfi_snapshot). Design: docs/design/sonicfi-switch-support.md.
 */

/** A SonicFi/CyberTan RSW switch, from the model or firmware string the controller holds.
 *  Mirrors models/device._NON_ECCLI_SWITCH_RE (sonicfi|cybertan|\brsw\d). */
export function isSonicfiSwitch(model: string | null | undefined, firmware?: string | null): boolean {
  const text = `${model ?? ""} ${firmware ?? ""}`.toLowerCase();
  return /sonicfi_rsw|cybertan|\brsw\d/.test(text);
}

export interface PortVlans {
  native: number;
  tagged: number[];
}

export interface PortDiff {
  port: string;
  before: PortVlans | null;
  after: PortVlans | null;
}

export interface AdoptGap {
  id: string;
  severity: "blocking" | "important";
  question: string;
  why: string;
}

export interface ConfigChange {
  path: string;
  op: "added" | "removed" | "changed";
  before: unknown;
  after: unknown;
}

export interface RunningPort extends PortVlans {
  mode: "trunk" | "access";
  section: string;
  extra_options: Record<string, unknown>;
}

export interface AdoptPreview {
  serial: string;
  model: string | null;
  firmware: string | null;
  owgw: { configuration: unknown; state_available: boolean | null };
  read_error: { code: string; message: string } | null;
  access: { mode: string | null; switch_host: string | null; gateway: string | null; credential_source: string | null } | null;
  snapshot: { read_at: string | null; firmware_version: string | null; active_config: string | null; saved_config_id: string | null } | null;
  running: { ports: Record<string, RunningPort>; vlan_database: number[]; anomalies: { port: string; code: string; detail: string }[] } | null;
  candidate: Record<string, unknown> | null;
  running_vs_candidate: PortDiff[] | null;
  boot_vs_candidate: PortDiff[] | null;
  owgw_vs_candidate: ConfigChange[] | null;
  factory_vs_candidate: ConfigChange[] | null;
  gaps: AdoptGap[];
  blocking: string[];
  no_op_equivalent: boolean;
  push_available: boolean;
  push_unavailable_reason: string;
}

/** "1 untagged, 1000 tagged" -- one port's VLANs in words. */
export function vlansText(v: PortVlans | null): string {
  if (!v) return "unknown";
  const tagged = v.tagged.length ? `, ${v.tagged.join(", ")} tagged` : "";
  return `${v.native} untagged${tagged}`;
}

/** "Ethernet23" -> "Port 24": the faceplate is 1-based, the agent's names are 0-based. */
export function faceplate(port: string): string {
  const m = port.match(/(\d+)$/);
  return m ? `Port ${Number(m[1]) + 1}` : port;
}

export type Verdict = "unread" | "no_op" | "changes";

/** The headline: has the switch been read, and would the first push change forwarding? */
export function adoptVerdict(p: AdoptPreview | undefined | null): { verdict: Verdict; text: string } {
  if (!p || !p.running || !p.running_vs_candidate) {
    return { verdict: "unread", text: "Not read yet. Read the switch to build its config of record." };
  }
  if (p.no_op_equivalent) {
    const n = p.boot_vs_candidate?.length ?? 0;
    return {
      verdict: "no_op",
      text:
        n === 0
          ? "No change: the config of record equals what the switch runs and what it boots."
          : `No change to forwarding. ${n} port${n === 1 ? "" : "s"} would keep VLANs a reboot drops today.`,
    };
  }
  const n = p.running_vs_candidate.length;
  return {
    verdict: "changes",
    text: n
      ? `The candidate would change ${n} port${n === 1 ? "" : "s"}. Do not push.`
      : "The candidate cannot be built faithfully. See the blocking items.",
  };
}

/** Trunk ports from the running read, faceplate order. */
export function runningTrunks(p: AdoptPreview | undefined | null): { port: string; vlans: PortVlans }[] {
  const ports = p?.running?.ports ?? {};
  return Object.entries(ports)
    .filter(([, v]) => v.mode === "trunk" || v.native !== 1)
    .map(([port, v]) => ({ port, vlans: { native: v.native, tagged: v.tagged } }))
    .sort((a, b) => Number(a.port.replace(/\D/g, "")) - Number(b.port.replace(/\D/g, "")));
}
